import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { ChartDonut } from '@phosphor-icons/react';
import { ChartColor } from '@t-one-internal/design-system';

interface ChannelRow {
    channel: string;
    count: number;
}

interface OrgChannelMixWidgetProps {
    data?: ChannelRow[] | null;
    loading?: boolean;
    error?: string;
}

const CHANNEL_CONFIG: Record<string, { label: string; color: string; bgClass: string; textClass: string; dotClass: string }> = {
    LMS: { label: 'E-Learning (LMS)', color: ChartColor.blue, bgClass: 'bg-blue-50', textClass: 'text-blue-600', dotClass: 'bg-blue-500' },
    TMS: { label: 'ILT & Events (TMS)', color: ChartColor.violet, bgClass: 'bg-purple-50', textClass: 'text-purple-600', dotClass: 'bg-purple-500' },
    Coaching: { label: 'Coaching', color: ChartColor.emerald, bgClass: 'bg-emerald-50', textClass: 'text-emerald-600', dotClass: 'bg-emerald-500' },
};

export const OrgChannelMixWidget: React.FC<OrgChannelMixWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    const { chartData, total } = useMemo(() => {
        const total = rows.reduce((sum, r) => sum + (Number(r.count) || 0), 0);
        const chartData = rows.map(r => ({
            name: r.channel,
            value: Number(r.count) || 0,
            color: CHANNEL_CONFIG[r.channel]?.color || '#9CA3AF',
        }));
        return { chartData, total };
    }, [rows]);

    return (
        <div className="flex flex-col h-full p-5">
<div className="flex-1">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <ChartDonut className="w-8 h-8" />
                        <span className="text-sm">No channel data</span>
                    </div>
                ) : (
                    <div className="flex items-center gap-6">
                        <div style={{ width: 150, height: 150 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={chartData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" strokeWidth={0}>
                                        {chartData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex-1 space-y-3">
                            {rows.map(r => {
                                const config = CHANNEL_CONFIG[r.channel];
                                const count = Number(r.count) || 0;
                                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                                return (
                                    <div key={r.channel} className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full ${config?.dotClass || 'bg-gray-400'}`} />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">{config?.label || r.channel}</div>
                                            <div className="text-[11px] text-gray-400">{count} {r.channel === 'LMS' ? 'enrollments' : r.channel === 'TMS' ? 'registrations' : r.channel === 'Coaching' ? 'engagements' : 'records'}</div>
                                        </div>
                                        <span className={`text-sm font-bold ${config?.textClass || 'text-gray-600'}`}>{pct}%</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
